import React, { useEffect, useState, useRef } from 'react';
import './ImageDialog.css';
import { siteTranslationMap, pathologyTranslationMap } from './FileUploadPopup';
import { API_BASE_URL } from './config';
import { normalizeAnnotationPoints } from './imageAnnotations';
// Inline SVG icons
const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
    </svg>
);

const ExpandIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707zm0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707zm-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707z"/>
    </svg>
);

const CompressIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M5.5 9a.5.5 0 0 1 .5.5v2.5a.5.5 0 0 1-1 0V9.5a.5.5 0 0 1 .5-.5zm5 0a.5.5 0 0 1 .5.5v2.5a.5.5 0 0 1-1 0V9.5a.5.5 0 0 1 .5-.5zM5.5 4a.5.5 0 0 1 .5.5v2.5a.5.5 0 0 1-1 0V4.5a.5.5 0 0 1 .5-.5zm5 0a.5.5 0 0 1 .5.5v2.5a.5.5 0 0 1-1 0V4.5a.5.5 0 0 1 .5-.5z"/>
        <path fillRule="evenodd" d="M.5 15a.5.5 0 0 0 .5.5h2.5a.5.5 0 0 0 0-1H1.5v-2a.5.5 0 0 0-1 0v2.5zm15 0a.5.5 0 0 1-.5.5h-2.5a.5.5 0 0 1 0-1H14.5v-2a.5.5 0 0 1 1 0v2.5zM.5 1a.5.5 0 0 1 .5-.5h2.5a.5.5 0 0 1 0 1H1.5v2a.5.5 0 0 1-1 0V1zm15 0a.5.5 0 0 0-.5-.5h-2.5a.5.5 0 0 0 0 1H14.5v2a.5.5 0 0 0 1 0V1z"/>
    </svg>
);

const ImageDialog = ({
    src,
    imageId: imageIdProp,
    onClose,
    annotations = [],
    onSaveAnnotations,
    allowAnnotations = false,
    showAnnotations = false,
    startInAnnotationMode = false,
}) => {
    // Handle both string and object src props
    const imageUrl = typeof src === 'string' ? src : src.url;
    const imageTitle = typeof src === 'string'
        ? src.split("/").pop()
        : (src.url ? src.url.split("/").pop() : 'Image');

    // Extract additional metadata if src is an object
    const hasMetadata = typeof src === 'object' && (src.tag || src.note || src.type_of_cancer);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isAnnotating, setIsAnnotating] = useState(allowAnnotations && startInAnnotationMode);
    const [savedPoints, setSavedPoints] = useState(() => normalizeAnnotationPoints(annotations));
    const [draftPoints, setDraftPoints] = useState(() => normalizeAnnotationPoints(annotations));
    const [annotationStatus, setAnnotationStatus] = useState('');
    const imageRef = useRef(null);
    const imageStageRef = useRef(null);
    const containerRef = useRef(null);
    const imageId = imageIdProp ?? (typeof src === 'object' ? (src.id ?? src.image_id) : null);
    const canAnnotate = allowAnnotations && typeof src === 'object';
    const canPersistAnnotation = imageId != null && typeof onSaveAnnotations === 'function';
    const visiblePoints = (canAnnotate || showAnnotations)
        ? (isAnnotating ? draftPoints : savedPoints)
        : [];

    useEffect(() => {
        const points = normalizeAnnotationPoints(annotations);
        setSavedPoints(points);
        setDraftPoints(points);
    }, [annotations]);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const downloadImage = () => {
        // Since the image is already loaded in the browser as a blob,
        // we can trigger a local download directly without contacting the server again.
        if (imageUrl.startsWith('blob:')) {
            const link = document.createElement('a');
            link.href = imageUrl;

            // Create a more descriptive filename if metadata is available
            let filename = 'image.jpg';
            if (typeof src === 'object') {
                const parts = [];
                if (src.site) parts.push(src.site);
                if (src.tag) parts.push(src.tag);
                if (parts.length > 0) {
                    // Sanitize parts for filename
                    filename = parts.join('_').replace(/[^a-zA-Z0-9_-]/g, '') + '.jpg';
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up the DOM
        } else {
             // Fallback for non-blob URLs, which shouldn't happen in the current flow
             // but is good practice to keep.
            const downloadUrl = (typeof src === 'object' && src.path) ? src.path : imageUrl;
            const apiUrl = `${API_BASE_URL}/api/v1/image/download/?url=${encodeURIComponent(downloadUrl)}`;
            window.open(apiUrl, '_blank');
        }
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(prevZoom => Math.max(0.5, Math.min(3, prevZoom + delta))); // Limit zoom between 0.5 and 3
    };

    const zoomIn = () => {
        setZoom(prevZoom => Math.min(3, prevZoom + 0.2));
    };

    const zoomOut = () => {
        setZoom(prevZoom => Math.max(0.5, prevZoom - 0.2));
    };

    const startAnnotation = () => {
        setDraftPoints(savedPoints);
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setAnnotationStatus('Click around the area of concern to place boundary points.');
        setIsAnnotating(true);
    };

    const handleAnnotationClick = (event) => {
        if (!isAnnotating || !imageStageRef.current) return;
        event.preventDefault();
        event.stopPropagation();

        const bounds = imageStageRef.current.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;

        const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
        const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
        setDraftPoints(points => [...points, {
            x: Number(x.toFixed(6)),
            y: Number(y.toFixed(6)),
        }]);
        setAnnotationStatus('');
    };

    const undoAnnotationPoint = () => {
        setDraftPoints(points => points.slice(0, -1));
    };

    const cancelAnnotation = () => {
        setDraftPoints(savedPoints);
        setAnnotationStatus('');
        setIsAnnotating(false);
    };

    const saveAnnotation = async () => {
        if (!canPersistAnnotation) {
            setAnnotationStatus('This image is missing its database ID. Close it and open it again from View Photos.');
            return;
        }

        const normalizedPoints = normalizeAnnotationPoints(draftPoints);
        try {
            await onSaveAnnotations(imageId, normalizedPoints);
            setSavedPoints(normalizedPoints);
            setDraftPoints(normalizedPoints);
            setAnnotationStatus(normalizedPoints.length
                ? 'Annotation updated. Submit annotations from the report to save.'
                : 'Annotation cleared. Submit annotations from the report to save.');
            setIsAnnotating(false);
        } catch (error) {
            setAnnotationStatus(error.message || 'Failed to save annotation.');
        }
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        const startX = e.clientX - pan.x;
        const startY = e.clientY - pan.y;

        const handleMouseMove = (e) => {
            e.preventDefault();
            const newX = (e.clientX - startX) / zoom;
            const newY = (e.clientY - startY) / zoom;

            setPan({ x: newX, y: newY });
        };

        const handleMouseUp = (e) => {
            e.preventDefault();
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <div className="image-dialog-overlay" onClick={onClose}>
            <div
                className={`image-dialog-container ${isFullScreen ? 'full-screen' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="image-dialog-header">
                    <h3 className="image-title">{imageTitle}</h3>
                    <div className="image-dialog-header-actions">
                        {canAnnotate && !isAnnotating && (
                            <button type="button" className="annotation-add-button" onClick={startAnnotation}>
                                {savedPoints.length ? 'Edit Annotation' : 'Add Annotation'}
                            </button>
                        )}
                        <button className="close-button" onClick={onClose}>×</button>
                    </div>
                </div>

                {/* Metadata Tags */}
                {hasMetadata && (
                    <div className="image-dialog-metadata" style={{ padding: '0 20px 10px', borderBottom: '1px solid #eee' }}>
                        {src.tag && (
                            <div className="metadata-section" style={{ marginBottom: '10px', marginTop: '10px' }}>
                                <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Tags:</span>
                                {src.tag.split(',').map((tag, index) => {
                                    const translatedTag = Object.keys(siteTranslationMap).find(
                                        key => siteTranslationMap[key] === tag.trim()
                                    ) || tag.trim();
                                    return (
                                        <span
                                            key={index}
                                            className="metadata-tag"
                                            style={{
                                                display: 'inline-block',
                                                backgroundColor: '#e1f5fe',
                                                color: '#0277bd',
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                marginRight: '5px',
                                                marginBottom: '5px',
                                                fontSize: '0.85em',
                                                fontWeight: 500,
                                                border: '1px solid #81d4fa',
                                            }}
                                        >
                                            {translatedTag}
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {src.type_of_cancer && (
                            <div className="metadata-section" style={{ marginBottom: '10px' }}>
                                <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Type of Cancer:</span>
                                <span
                                    className="metadata-tag"
                                    style={{
                                        display: 'inline-block',
                                        backgroundColor: '#f9e4e8',
                                        color: '#c2185b',
                                        padding: '4px 10px',
                                        borderRadius: '20px',
                                        fontSize: '0.85em',
                                        fontWeight: 500,
                                        border: '1px solid #f06292',
                                    }}
                                >
                                    {Object.entries(pathologyTranslationMap).find(
                                        ([label, value]) => value === src.type_of_cancer
                                    )?.[0] || src.type_of_cancer}
                                </span>
                            </div>
                        )}
                        {src.note && (
                            <div style={{ marginTop: '5px' }}>
                                <span style={{ fontWeight: 'bold' }}>Note:</span> {src.note}
                            </div>
                        )}
                    </div>
                )}

                {/* Image */}
                <div
                    className={`image-dialog-content ${isAnnotating ? 'annotation-mode' : ''}`}
                    onWheel={handleWheel}
                    onMouseDown={isAnnotating ? undefined : handleMouseDown}
                    ref={containerRef}
                >
                    <div
                        ref={imageStageRef}
                        className={`annotation-image-stage ${isAnnotating ? 'is-editing' : ''}`}
                        onClick={handleAnnotationClick}
                        style={{
                            transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                            transformOrigin: 'center',
                        }}
                    >
                        <img
                            src={imageUrl}
                            alt="Zoomable"
                            className="zoomable-image"
                            draggable="false"
                            ref={imageRef}
                        />
                        {visiblePoints.length > 0 && (
                            <svg
                                className="image-annotation-layer"
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                aria-label={`Annotation with ${visiblePoints.length} points`}
                            >
                                {visiblePoints.length >= 3 ? (
                                    <polygon
                                        className="annotation-polygon"
                                        points={visiblePoints.map(point => `${point.x * 100},${point.y * 100}`).join(' ')}
                                    />
                                ) : (
                                    <polyline
                                        className="annotation-polyline"
                                        points={visiblePoints.map(point => `${point.x * 100},${point.y * 100}`).join(' ')}
                                    />
                                )}
                                {visiblePoints.map((point, index) => (
                                    <circle
                                        key={`${point.x}-${point.y}-${index}`}
                                        className="annotation-point"
                                        cx={point.x * 100}
                                        cy={point.y * 100}
                                        r="1.4"
                                    />
                                ))}
                            </svg>
                        )}
                    </div>
                </div>
                <div className="image-dialog-actions">
                    <div className="annotation-action-group">
                        {canAnnotate && isAnnotating && (
                            <>
                                <span className="annotation-count">{draftPoints.length} point{draftPoints.length === 1 ? '' : 's'}</span>
                                <button type="button" className="annotation-secondary-button" onClick={undoAnnotationPoint} disabled={!draftPoints.length}>Undo</button>
                                <button type="button" className="annotation-secondary-button" onClick={() => setDraftPoints([])} disabled={!draftPoints.length}>Clear</button>
                                <button type="button" className="annotation-secondary-button" onClick={cancelAnnotation}>Cancel</button>
                                <button type="button" className="annotation-save-button" onClick={saveAnnotation}>Save Annotation</button>
                            </>
                        )}
                        {annotationStatus && <span className="annotation-status" role="status">{annotationStatus}</span>}
                    </div>
                    <div className="image-view-action-group">
                        <button className="icon-btn" onClick={zoomOut} title="Zoom Out" disabled={isAnnotating}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M3 8a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 8z"/>
                            </svg>
                        </button>
                        <button className="icon-btn" onClick={zoomIn} title="Zoom In" disabled={isAnnotating}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M8 3a.5.5 0 0 1 .5.5v4h4a.5.5 0 0 1 0 1h-4v4a.5.5 0 0 1-1 0v-4h-4a.5.5 0 0 1 0-1h4v-4A.5.5 0 0 1 8 3z"/>
                            </svg>
                        </button>
                        <button className="icon-btn" onClick={toggleFullScreen} title={isFullScreen ? "Exit Full Screen" : "Full Screen"}>
                            {isFullScreen ? <CompressIcon /> : <ExpandIcon />}
                        </button>
                        <button className="icon-btn" onClick={downloadImage} title="Download">
                            <DownloadIcon />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageDialog;
